import { useRequestVetterJobAccessCodeMutation, useVetterJobLoginMutation } from '@codezo/graphql';
import { ArrowLeft, ArrowRight, Loader2, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../hooks/useAuth';
import { clearJobLinkToken, setJobLinkToken } from '../../lib/job-session';
import { classifyLinkFailure } from '../../lib/link-failure';
import { JobNotice } from './JobShell';

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const CODE_LENGTH = 6;
/** Backend rate-limits resends; keep the button quiet for a beat rather than letting it 429. */
const RESEND_COOLDOWN_SECONDS = 30;

interface JobAuthChallengeProps {
	/** The opaque `?token=` from the emailed link. Both mutations require it. */
	linkToken: string;
	/** Shown when an established session ended rather than on a first visit. */
	expired?: boolean;
	/** Fires once the access token is in context. */
	onAuthenticated: () => void;
	/** The link is dead — revoked or replaced. The caller swaps to a terminal screen. */
	onDeadLink: (message: string) => void;
}

/**
 * The email → code challenge behind a vetter link.
 *
 * Verifying sets a real vetter refresh cookie bound to this one job, so a
 * reload restores the session with a plain refresh. The access token is short
 * (30 min); a lapse is handled by the shared reauth route.
 */
export function JobAuthChallenge({
	linkToken,
	expired = false,
	onAuthenticated,
	onDeadLink,
}: JobAuthChallengeProps) {
	const { setAccessToken } = useAuth();

	const [step, setStep] = useState<'email' | 'code'>('email');
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [cooldown, setCooldown] = useState(0);

	const [requestCode, { loading: requesting }] = useRequestVetterJobAccessCodeMutation();
	const [login, { loading: verifying }] = useVetterJobLoginMutation();

	useEffect(() => {
		if (cooldown <= 0) return;
		const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldown]);

	const trimmedEmail = email.trim();
	const emailValid = EMAIL_RE.test(trimmedEmail);
	const codeValid = code.trim().length === CODE_LENGTH;

	const killLink = (message?: string | null) => {
		clearJobLinkToken();
		onDeadLink(message || 'This link is no longer active.');
	};

	const handleRequestCode = async (isResend = false) => {
		if (!emailValid || requesting) return;
		setError(null);
		try {
			const { data } = await requestCode({
				variables: { input: { token: linkToken, email: trimmedEmail } },
			});
			const res = data?.requestVetterJobAccessCode;
			if (res && res.success === false) {
				if (classifyLinkFailure(res.message, res.messageKey) === 'terminal') {
					killLink(res.message);
					return;
				}
				setError(res.message || 'Could not send a code. Try again.');
				return;
			}
			// Deliberately neutral — a wrong address looks exactly like a right
			// one, so always advance. A bad address simply never gets a code.
			setStep('code');
			setCooldown(RESEND_COOLDOWN_SECONDS);
			if (isResend) setCode('');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not send a code. Try again.');
		}
	};

	const handleVerify = async () => {
		if (!codeValid || verifying) return;
		setError(null);
		try {
			const { data } = await login({
				variables: { input: { token: linkToken, email: trimmedEmail, code: code.trim() } },
			});
			const res = data?.vetterJobLogin;
			if (!res?.success || !res.token) {
				if (classifyLinkFailure(res?.message) === 'terminal') {
					killLink(res?.message);
					return;
				}
				setError(res?.message || 'That code could not be verified.');
				return;
			}
			setJobLinkToken(linkToken);
			setAccessToken(res.token);
			onAuthenticated();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
		}
	};

	if (step === 'email') {
		return (
			<>
				<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">
					{expired ? 'Your session has ended' : 'Confirm your email'}
				</h1>
				<p className="mt-2 text-sm text-text-3">
					{expired
						? 'Confirm your email and we’ll send a new code to get you back in.'
						: 'Enter the email address this link was sent to. We’ll send you a one-time code — no account needed.'}
				</p>

				<div className="mt-6 flex flex-col gap-1.5">
					<Label>Email address</Label>
					<Input
						autoFocus
						type="email"
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							if (error) setError(null);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleRequestCode();
							}
						}}
						placeholder="name@company.com"
						disabled={requesting}
					/>
				</div>

				{error && (
					<div className="mt-3">
						<JobNotice title={error} />
					</div>
				)}

				<Button
					className="mt-5 w-full"
					onClick={() => handleRequestCode()}
					disabled={!emailValid || requesting}
				>
					{requesting ? (
						<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
					) : (
						<ArrowRight size={14} data-icon="inline-start" />
					)}
					{requesting ? 'Sending…' : 'Send me a code'}
				</Button>

				<Footnote />
			</>
		);
	}

	return (
		<>
			<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">Enter your code</h1>
			<p className="mt-2 text-sm text-text-3">
				We sent a {CODE_LENGTH}-digit code to{' '}
				<span className="text-text-2">{trimmedEmail}</span>. It expires shortly.
			</p>

			<div className="mt-6 flex flex-col gap-1.5">
				<Label>Verification code</Label>
				<Input
					autoFocus
					inputMode="numeric"
					autoComplete="one-time-code"
					maxLength={CODE_LENGTH}
					value={code}
					onChange={(e) => {
						setCode(e.target.value.replace(/\D/g, ''));
						if (error) setError(null);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleVerify();
						}
					}}
					placeholder="123456"
					className="text-center tracking-[0.4em]"
					disabled={verifying}
				/>
			</div>

			{error && (
				<div className="mt-3">
					<JobNotice title={error} detail="Codes expire — request a new one below." />
				</div>
			)}

			<Button
				className="mt-5 w-full"
				onClick={handleVerify}
				disabled={!codeValid || verifying}
			>
				{verifying ? (
					<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
				) : (
					<ArrowRight size={14} data-icon="inline-start" />
				)}
				{verifying ? 'Verifying…' : 'Open price request'}
			</Button>

			<div className="mt-4 flex items-center justify-between gap-3">
				<button
					onClick={() => {
						setStep('email');
						setCode('');
						setError(null);
					}}
					className="flex items-center gap-1 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
					disabled={verifying}
				>
					<ArrowLeft size={12} />
					Change email
				</button>
				<button
					onClick={() => handleRequestCode(true)}
					className="bg-transparent text-sm text-text-3 transition-colors hover:text-text-1 disabled:cursor-not-allowed disabled:text-text-4 disabled:hover:text-text-4"
					disabled={cooldown > 0 || requesting}
				>
					{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
				</button>
			</div>

			<Footnote />
		</>
	);
}

function Footnote() {
	return (
		<div className="mt-5 flex items-start gap-2 text-sm text-text-4">
			<Mail size={14} className="mt-0.5 shrink-0" />
			<span>
				Only the address the link was sent to can open it — that&apos;s what the code
				checks. The sender can revoke a link at any time.
			</span>
		</div>
	);
}
